import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Truck, Gauge, ClipboardCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAFETY_ITEMS = [
  { key: 'brakes', label: 'Brakes' },
  { key: 'lights', label: 'Lights & Signals' },
  { key: 'tires', label: 'Tires' },
  { key: 'fluids', label: 'Fluids (oil/coolant/washer)' },
  { key: 'coupling', label: 'Coupling / 5th wheel' },
  { key: 'horn_wipers', label: 'Horn & Wipers' },
  { key: 'mirrors', label: 'Mirrors & Glass' },
  { key: 'emergency_kit', label: 'Emergency Kit / Triangles' },
];

const PreTripModal = ({ load, onClose, onComplete }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truckId, setTruckId] = useState('');
  const [trailerId, setTrailerId] = useState('');
  const [odometer, setOdometer] = useState('');
  const [safety, setSafety] = useState({});
  const [issues, setIssues] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/assets/me`, { withCredentials: true });
        setAssets(res.data);
        const truck = res.data.find(a => a.asset_type === 'truck' && a.status === 'active');
        const trailer = res.data.find(a => a.asset_type === 'trailer' && a.status === 'active');
        if (truck) {
          setTruckId(truck.id);
          setOdometer(String(Math.round(truck.current_odometer || 0)));
        }
        if (trailer) setTrailerId(trailer.id);
      } catch (e) {
        setError('Could not load your assigned assets.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const trucks = assets.filter(a => a.asset_type === 'truck' && a.status === 'active');
  const trailers = assets.filter(a => a.asset_type === 'trailer' && a.status === 'active');
  const selectedTruck = trucks.find(t => t.id === truckId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!truckId) { setError('Please select a truck.'); return; }
    if (!odometer || isNaN(parseFloat(odometer))) { setError('Please enter a valid odometer reading.'); return; }
    const odoVal = parseFloat(odometer);
    if (selectedTruck && odoVal < (selectedTruck.current_odometer || 0) - 1) {
      setError(`Odometer (${odoVal}) is lower than the truck's last reading (${selectedTruck.current_odometer}). Please double-check.`);
      return;
    }
    const allChecked = SAFETY_ITEMS.every(it => safety[it.key]);
    if (!allChecked && !issues.trim()) {
      setError('Some safety items are unchecked. Either confirm all items or describe issues below.');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/loads/${load.id}/start-trip`,
        {
          truck_asset_id: truckId,
          trailer_asset_id: trailerId || null,
          odometer: odoVal,
          safety_checks: safety,
          issues: issues || null,
          notes: notes || null,
        },
        { withCredentials: true }
      );
      onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start trip.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="pre-trip-modal">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#002FA7]/10 rounded-md flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-[#002FA7]" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Pre-Trip Inspection</h2>
              <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Load {load.load_ref || load.id.slice(-6)}</p>
            </div>
          </div>
          <button data-testid="close-pre-trip" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="p-8 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading your assets...</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {trucks.length === 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-start gap-2" data-testid="no-truck-warning">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>You have no active trucks assigned. Ask your admin to assign one before starting a trip.</p>
              </div>
            )}

            {/* Asset selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Truck *</label>
                <select data-testid="pre-trip-truck" value={truckId} onChange={(e) => { setTruckId(e.target.value); const t = trucks.find(x => x.id === e.target.value); if (t) setOdometer(String(Math.round(t.current_odometer || 0))); }} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                  <option value="">— Select truck —</option>
                  {trucks.map(t => <option key={t.id} value={t.id}>{t.label}{t.license_plate ? ` (${t.license_plate})` : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Trailer (optional)</label>
                <select data-testid="pre-trip-trailer" value={trailerId} onChange={(e) => setTrailerId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm">
                  <option value="">— None —</option>
                  {trailers.map(t => <option key={t.id} value={t.id}>{t.label}{t.license_plate ? ` (${t.license_plate})` : ''}</option>)}
                </select>
              </div>
            </div>

            {/* Odometer */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Starting Odometer (mi) *</label>
              <div className="relative">
                <Gauge className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="pre-trip-odometer" type="number" required min="0" step="1" value={odometer} onChange={(e) => setOdometer(e.target.value)} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-base font-semibold" style={{ fontFamily: 'Work Sans, sans-serif' }} />
              </div>
              {selectedTruck && (
                <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Last recorded: {Math.round(selectedTruck.current_odometer || 0).toLocaleString()} mi</p>
              )}
            </div>

            {/* Safety checklist */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Safety Checks</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SAFETY_ITEMS.map(it => (
                  <label key={it.key} data-testid={`safety-${it.key}`} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-all ${safety[it.key] ? 'border-green-400 bg-green-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}>
                    <input type="checkbox" checked={!!safety[it.key]} onChange={(e) => setSafety({ ...safety, [it.key]: e.target.checked })} className="w-4 h-4 accent-[#34C759]" />
                    <span className="text-sm text-slate-800" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{it.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Issues / notes */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Issues found (required if any safety item is unchecked)</label>
              <textarea data-testid="pre-trip-issues" rows="2" value={issues} onChange={(e) => setIssues(e.target.value)} placeholder="Describe any defects, leaks, damages..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Notes</label>
              <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800" data-testid="pre-trip-error" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Cancel</button>
              <button data-testid="submit-pre-trip" type="submit" disabled={submitting || trucks.length === 0} className="px-5 py-2 bg-[#34C759] text-white rounded-md text-sm font-semibold hover:bg-green-700 transition-all disabled:opacity-50 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <Truck className="w-4 h-4" />
                {submitting ? 'Starting...' : 'Start Trip'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PreTripModal;
